import { faCircleDollar } from '@fortawesome/pro-regular-svg-icons'

import { TextColors, TypographyStyles } from '@/styles'
import { Card, CardColors, Icon, Separator, Text } from '@/ui'

import { BreakdownTooltip } from '../estimate-breakdown-v3/shared/breakdown-tooltip'
import { CalculationRow } from '../estimate-breakdown-v3/shared/calculation-row'
import { centsToEstimateAmount, centsToSubtractedAmount } from '../utils'
import {
  StateShareTooltip,
  StateStandardDeductionTooltip,
  StateTaxableIncomeTooltip,
} from './StateBreakdownTooltips'
import type { StateBreakdownData } from './stateBreakdownData'

export const StateIncomeSection = ({ data }: { data: StateBreakdownData }) => (
  <>
    <div className="flex items-center mb-4">
      <Text variant={TypographyStyles.H2} className="flex items-center">
        <Icon icon={faCircleDollar} className="mr-2" />
        Income
      </Text>
    </div>

    <Text className="w-full lg:w-8/12 mb-6" color={TextColors.MUTED}>
      {data.isHomeState
        ? `This is the income ${data.state} taxes, and the deduction ${data.state} applies to it.`
        : `${data.state} taxes the share of your practice income you earned there.`}
    </Text>

    {data.isHomeState && data.hasOtherStates && (
      <Card color={CardColors.LEVEL_1} className="mb-6">
        <div className="flex flex-col space-y-2 p-4">
          <Text variant={TypographyStyles.H3}>
            Why your states add up to more than 100%
          </Text>
          <Text color={TextColors.MUTED}>
            Each other state also taxes the share you earned there, so the
            amounts overlap with {data.state}&apos;s. Many states let you claim
            a credit at tax time for tax paid to another state; we don&apos;t
            apply those credits to quarterly estimates yet, so these estimates
            lean high rather than leaving you short at the end of the year.
          </Text>
        </div>
      </Card>
    )}

    {!data.isHomeState && data.apportionment && (
      <>
        <CalculationRow
          left={<Text>Projected practice income</Text>}
          right={
            <Text variant={TypographyStyles.H3}>
              {centsToEstimateAmount(data.apportionment.baseInCents)}
            </Text>
          }
        />
        <CalculationRow
          left={
            <BreakdownTooltip
              trigger={`Share you earned in ${data.state}`}
              content={<StateShareTooltip filingState={data.state} />}
            />
          }
          right={
            <Text variant={TypographyStyles.H3}>
              {data.apportionment.percentage}%
            </Text>
          }
        />
      </>
    )}

    <CalculationRow
      left={
        <Text>
          {data.isHomeState
            ? 'Income we expect for the year'
            : `Share of your income earned in ${data.state}`}
        </Text>
      }
      right={
        <Text variant={TypographyStyles.H3}>
          {centsToEstimateAmount(data.incomeInCents)}
        </Text>
      }
    />

    {data.stateStandardDeductionInCents !== null && (
      <CalculationRow
        left={
          <>
            <BreakdownTooltip
              trigger={`Income ${data.state} doesn't tax`}
              content={
                <StateStandardDeductionTooltip filingState={data.state} />
              }
            />
            {data.stateStandardDeductionInCents === 0 && (
              <Text color={TextColors.MUTED}>
                <i>{data.state} sets nothing aside untaxed.</i>
              </Text>
            )}
          </>
        }
        right={
          <Text variant={TypographyStyles.H3}>
            {centsToSubtractedAmount(data.stateStandardDeductionInCents)}
          </Text>
        }
      />
    )}

    {data.taxableIncomeInCents !== null && (
      <>
        <Separator />
        <CalculationRow
          left={
            <BreakdownTooltip
              trigger={
                <Text variant={TypographyStyles.H3}>
                  Income {data.state} taxes
                </Text>
              }
              content={<StateTaxableIncomeTooltip filingState={data.state} />}
            />
          }
          right={
            <Text variant={TypographyStyles.H3}>
              {centsToEstimateAmount(data.taxableIncomeInCents)}
            </Text>
          }
        />
      </>
    )}

    <Text color={TextColors.MUTED} className="w-full md:w-2/3 italic mt-4">
      {data.state} works out your income differently from the IRS. It leaves its
      own amount untaxed, and the federal write-offs for business income and
      self-employment tax don&apos;t lower what {data.state} taxes — so this
      number is usually higher than the federal one.
    </Text>

    {!data.isHomeState && (
      <Text color={TextColors.MUTED} className="w-full md:w-2/3 italic mt-2">
        Your home state is taxed on this income too, because it taxes your whole
        household income. We don&apos;t apply credits between states to
        quarterly estimates yet, so your states add up to more than 100% of your
        income.
      </Text>
    )}
  </>
)
